import axios from 'axios';
import { config } from '../lib/config.js';

/**
 * The single HTTP client. Everything in `services/` goes through this.
 *
 * `withCredentials` is on because the refresh token is an httpOnly cookie — the
 * short-lived access token is held in memory only, so a stolen XSS payload
 * cannot read it out of localStorage.
 */
export const http = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
  timeout: 20_000,
  headers: { Accept: 'application/json' },
});

/** In-memory access token. Deliberately NOT persisted. */
let accessToken = null;

export function setAccessToken(token) {
  accessToken = token ?? null;
}

export function getAccessToken() {
  return accessToken;
}

/**
 * Called when a session cannot be recovered — a revoked, banned or expired
 * refresh token. The auth store registers itself here rather than being imported
 * directly, because it already imports this module.
 */
let onSessionLost = null;

export function setSessionLostHandler(handler) {
  onSessionLost = handler;
}

/* The CSRF cookie is readable by JS on purpose: the double-submit check compares
   it against this header, and an attacker on another origin can send the cookie
   but cannot read it to build the header. See middleware/csrf.js. */
const CSRF_COOKIE = 'cc_csrf';
const SAFE_METHODS = new Set(['get', 'head', 'options']);

function readCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

http.interceptors.request.use((request) => {
  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (!SAFE_METHODS.has((request.method ?? 'get').toLowerCase())) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) request.headers['X-CSRF-Token'] = decodeURIComponent(csrf);
  }

  return request;
});

/**
 * Error shape normalisation. The backend always answers
 * `{ success: false, error: { code, message, details } }`, so every consumer can
 * rely on `err.code`, `err.message` and `err.details` regardless of whether the
 * failure came from the API, a network drop, or a timeout.
 */
export class ApiClientError extends Error {
  constructor({ status, code, message, details }) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Silent re-authentication.
 *
 * A 401 that says the access token has expired (or was retired by a password
 * change) is recoverable: the refresh cookie is still good, so we mint a new
 * access token and replay the original request once. Two rules keep this from
 * turning into a loop or a stampede —
 *
 *   · one refresh in flight at a time, shared by every request that failed
 *     while it was running, so ten parallel 401s cause one POST /auth/refresh;
 *   · a request is retried at most once, and /auth/refresh itself is never
 *     retried. If refresh fails the session is genuinely over.
 *
 * TOKEN_STALE is included because it is what the server returns when
 * `tokenVersion` has moved on — a password change on another tab, say. The
 * refresh either succeeds with the new version or fails and signs us out; both
 * are correct. A banned or timed-out account gets ACCOUNT_* codes instead, which
 * are deliberately absent here: no amount of refreshing fixes those.
 */
const REFRESHABLE = new Set(['TOKEN_EXPIRED', 'TOKEN_STALE']);

let refreshPromise = null;

function canRetry(config) {
  return Boolean(config) && !config._retried && !config.url?.includes('/auth/refresh');
}

export function refreshSession() {
  refreshPromise ??= http
    .post('/auth/refresh')
    .then((session) => {
      setAccessToken(session.accessToken);
      return session;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

http.interceptors.response.use(
  /**
   * Callers get the payload, not the envelope. List endpoints put paging in a
   * `meta` sibling of `data`, which that unwrapping would throw away — so a
   * caller that needs it asks for it with `{ withMeta: true }` in the request
   * config and receives `{ data, meta }` instead. Opt-in, because the vast
   * majority of requests want the one useful object and nothing around it.
   */
  (response) => {
    const body = response.data;
    if (response.config?.withMeta) return { data: body?.data ?? body, meta: body?.meta ?? null };
    return body?.data ?? body;
  },
  async (error) => {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      throw new ApiClientError({
        status: 0,
        code: 'TIMEOUT',
        message: 'That took too long. Please try again.',
      });
    }

    if (!error.response) {
      throw new ApiClientError({
        status: 0,
        code: 'NETWORK_ERROR',
        message: 'Could not reach Chapters&Co. Check your connection and try again.',
      });
    }

    const { status, data } = error.response;
    const code = data?.error?.code ?? 'UNKNOWN';

    if (status === 401 && REFRESHABLE.has(code) && canRetry(error.config)) {
      error.config._retried = true;
      try {
        await refreshSession();
        return http(error.config);
      } catch {
        onSessionLost?.();
        throw new ApiClientError({
          status: 401,
          code: 'SESSION_EXPIRED',
          message: 'Your session has ended. Please sign in again.',
        });
      }
    }

    throw new ApiClientError({
      status,
      code,
      message: data?.error?.message ?? 'Something went wrong.',
      details: data?.error?.details,
    });
  },
);

export default http;
